<script setup>
import moment from 'moment'
import notificationService from '@/services/notification.service'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

definePage({
  meta: {
    name: 'notifications',
  },
})

const route = useRoute()
const moduleCode = computed(() => route.meta.module)

const searchQuery = ref('')
const statusFilter = ref(null)
const dateRange = ref(null)

const searchParams = computed(() => {
  const params = {}

  if (statusFilter.value === 'read')
    params.read_at = 'not-null'
  else if (statusFilter.value === 'unread')
    params.read_at = 'null'

  // else if (statusFilter.value === 'cleared')
  //   params.is_clear = true

  if (dateRange.value) {
    const dates = dateRange.value.split(' to ')

    if (dates.length >= 1)
      params.created_at = [dates[0], dates[1] ?? dates[0]]
  }

  return params
})

const q = computed(() => searchQuery.value || undefined)

const page = ref(1)
const itemsPerPage = ref(20)

const {
  isFetching,
  data: notificationsData,
  execute: fetchNotifications,
} = useApi(createUrl('/configurations/notifications', {
  query: {
    search: searchParams,
    q,
    module_code: moduleCode,
    paged: 1,
    page,
    itemsPerPage,
  },
}))

const notifications = computed(() => notificationsData.value?.data ?? [])
const totalUnreadCount = computed(() => notificationsData.value?.un_read_count ?? 0)
const totalRecords = computed(() => notificationsData.value?.meta?.total ?? 0)
const totalPages = computed(() => Math.ceil(totalRecords.value / itemsPerPage.value))

const markRead = async notificationIds => {
  if (notificationIds.length === 1)
    await notificationService.markNotification(notificationIds[0])
  else
    await notificationService.markAllNotifications()

  fetchNotifications()
}

// const removeNotification = async notificationId => {
//   await notificationService.clearSingleNotification(notificationId)
//   fetchNotifications()
// }

// const clearAllNotifications = async () => {
//   await notificationService.clearAllNotifications()
//   fetchNotifications()
// }

const handleNotificationClick = notification => {
  if (!notification.is_seen) {
    notificationService.markNotification(notification.id).then(() => fetchNotifications())
  }
  if (notification?.action_url) {
    window.open(notification.action_url, '_blank')
  }
}

const resetFilters = () => {
  searchQuery.value = ''
  statusFilter.value = null
  dateRange.value = null
  page.value = 1
}

watch([searchQuery, statusFilter, dateRange], () => {
  page.value = 1
})

const statusOptions = [
  { title: 'Unread', value: 'unread' },
  { title: 'Read', value: 'read' },

  // { title: 'Cleared', value: 'cleared' },
]

const isAllMarkRead = computed(() => notifications.value.some(item => item.is_seen === false))
</script>

<template>
  <section>
    <!-- Filters -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">Notifications</span>
        </template>
        <template #subtitle>
          <span class="text-body-2 text-medium-emphasis">
            <!-- {{ totalUnreadCount }} unread notifications -->
            <!-- You have {{ totalUnreadCount }} unread notification{{ totalUnreadCount > 1 ? 's' : '' }}. -->
            All the activities and updates will appear here.
          </span>
        </template>
        <template #append>
          <div class="d-flex align-center gap-2">
            <VBtn
              v-if="notifications.length && isAllMarkRead"
              variant="tonal"
              color="warning"
              size="small"
              prepend-icon="tabler-mail-opened"
              @click="markRead([])"
            >
              Mark All Read
            </VBtn>
            <!--
              <VBtn
              v-if="notifications.length"
              variant="tonal"
              size="small"
              color="error"
              prepend-icon="tabler-trash"
              @click="clearAllNotifications"
              >
              Clear All
              </VBtn> 
            -->
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText>
        <VRow>
          <VCol
            cols="12"
            md="4"
          >
            <AppTextField
              v-model="searchQuery"
              label="Search"
              placeholder="Search by notification"
              clearable
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
          >
            <AppSelect
              v-model="statusFilter"
              label="Status"
              :items="statusOptions"
              placeholder="Filter by status"
              clearable
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
          >
            <AppDateTimePicker
              v-model="dateRange"
              label="Date Range"
              placeholder="Filter by date range"
              :config="{ mode: 'range', maxDate: 'today' }"
              clearable
            />
          </VCol>
        </VRow>
      </VCardText>

      <!--
        <VCardActions v-if="searchQuery || statusFilter !== null || dateRange">
        <VBtn
        color="primary"
        variant="tonal"
        size="small"
        @click="resetFilters"
        >
        Reset Filters
        </VBtn>
        </VCardActions> 
      -->
    </VCard>

    <!-- Notification List -->
    <VCard
      variant="flat"
      class="border"
    >
      <VList
        v-if="notifications.length"
        class="notification-list py-0"
      >
        <template
          v-for="(notification, index) in notifications"
          :key="notification.id"
        >
          <VDivider v-if="index > 0" />
          <VListItem
            link
            lines="one"
            min-height="72px"
            class="list-item-hover-class px-6 py-4"
            @click="handleNotificationClick(notification)"
          >
            <!--
              <template #prepend>
              <VListItemAction start>
              <VAvatar
              size="40"
              :color="notification.color && notification.icon ? notification.color : undefined"
              :image="notification.img || undefined"
              :icon="notification.icon || undefined"
              :variant="notification.img ? undefined : 'tonal'"
              >
              <span v-if="notification.text">{{ avatarText(notification.text) }}</span>
              </VAvatar>
              </VListItemAction>
              </template> 
            -->

            <VListItemTitle class="font-weight-medium">
              {{ notification.title }}
            </VListItemTitle>
            <VListItemSubtitle>{{ notification.subtitle }}</VListItemSubtitle>
            <div class="mt-1 d-flex align-center gap-2">
              <div class="text-xs text-disabled">
                {{ notification.time }}
              </div>
              <VDivider
                v-if="notification.read_at"
                vertical
              />
              <div
                v-if="notification.read_at"
                class="text-xs text-primary"
              >
                Read At: {{ moment(notification.read_at).format('DD MMM YYYY, hh:mm A') }}
              </div>
            </div>

            <template #append>
              <div class="d-flex align-center gap-2">
                <IconBtn
                  v-if="notification.action_url"
                  size="small"
                  class="visible-in-hover"
                  @click.stop="handleNotificationClick(notification)"
                >
                  <VIcon
                    size="20"
                    icon="tabler-external-link"
                  />
                  <VTooltip
                    activator="parent"
                    location="top"
                  >
                    Open
                  </VTooltip>
                </IconBtn>

                <IconBtn
                  v-if="!notification.is_seen"
                  size="small"
                  class="visible-in-hover"
                  @click.stop="markRead([notification.id])"
                >
                  <VIcon
                    size="20"
                    icon="tabler-mail-opened"
                  />
                  <VTooltip
                    activator="parent"
                    location="top"
                  >
                    Mark as Read
                  </VTooltip>
                </IconBtn>

                <!--
                  <IconBtn
                  size="small"
                  class="visible-in-hover"
                  @click.stop="removeNotification(notification.id)"
                  >
                  <VIcon
                  size="20"
                  icon="tabler-x"
                  />
                  <VTooltip
                  activator="parent"
                  location="top"
                  >
                  Clear
                  </VTooltip>
                  </IconBtn> 
                -->
              </div>
            </template>
          </VListItem>
        </template>
      </VList>

      <!-- Empty State -->
      <VCardText
        v-else
        class="text-center py-12"
      >
        <VIcon
          icon="tabler-bell-off"
          size="48"
          class="text-disabled mb-4"
        />
        <p class="text-body-1 text-medium-emphasis">
          No Notifications Found!
        </p>
      </VCardText>

      <!-- Loading Overlay -->
      <VOverlay
        v-model="isFetching"
        contained
        persistent
        class="align-center justify-center"
      >
        <VProgressCircular indeterminate />
      </VOverlay>

      <!-- Pagination -->
      <template v-if="totalPages > 1">
        <VDivider />
        <VCardText class="d-flex align-center justify-center">
          <VPagination
            v-model="page"
            :length="totalPages"
            :total-visible="5"
            density="comfortable"
          />
        </VCardText>
      </template>
    </VCard>
  </section>
</template>

<style lang="scss">
.list-item-hover-class {
  .visible-in-hover {
    display: none;
  }

  &:hover {
    .visible-in-hover {
      display: block;
    }
  }
}
</style>
